"use client";

import { useMemo } from "react";
import type { GhostStreamItem } from "@/lib/atlas/useAtlasGhostStream";

export interface GhostStreamProps {
  items: GhostStreamItem[];
  primaryColor: string;
  secondaryColor: string;
  className?: string;
}

/** Approximate luminance (0–255) for theme-aware opacity. */
function hexLuminance(hex: string): number {
  const n = parseInt(hex.replace(/^#/, ""), 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Masked, blurred "ghost" view of Atlas thought stream for the processing overlay.
 * Reuses Atlas data; condensed meta labels only. Theme-aware opacity so it reads on strong colors.
 */
export default function GhostStream({ items, primaryColor, secondaryColor, className = "" }: GhostStreamProps) {
  const opacity = useMemo(() => {
    const lum = hexLuminance(primaryColor);
    if (lum < 80) return 0.38;
    if (lum < 160) return 0.32;
    return 0.28;
  }, [primaryColor]);

  return (
    <div
      className={`absolute inset-0 overflow-hidden rounded-2xl pointer-events-none ${className}`}
      style={{
        maskImage: "linear-gradient(to bottom, black 0%, transparent 85%)",
        WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 85%)",
        opacity,
        filter: "blur(5px)",
        WebkitFilter: "blur(5px)",
      }}
      aria-hidden="true"
    >
      <div className="absolute inset-0 flex flex-col justify-end gap-0.5 px-3 pb-2">
        {items.slice(-6).map((item) => (
          <div
            key={item.id}
            className="text-[10px] font-medium truncate transition-opacity duration-300"
            style={{
              color: item.completed ? primaryColor : secondaryColor,
              opacity: item.active ? 1 : 0.6,
            }}
          >
            {item.completed && "✓ "}
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
